// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's edits (frontend-u0o.9, slice S2): the Egenskaber
 * panel's editable fields per element, the `POST /model/edits` ops a field
 * change or a per-field reset makes, Slet and Tilbage til detekteret, and
 * the optimistic overlay that shows a batch before the server has answered.
 *
 * Coordinates are world, under the GET names, so a value read and sent back
 * round-trips (ruxd-5uv). The per-field reset reads `metadata.edited_from`,
 * the detected record the server keeps in the row's **level frame**: only
 * the fields a rigid level frame leaves alone (lengths, heights, thickness,
 * kinds, names) can be reset one by one; a position goes back with
 * Tilbage til detekteret (`*.revert`). Pure: no React, no DOM.
 */

import type { ModelEditOp, SolidOpening, SolidSlab, SolidSpace, SolidWall, Storey } from '../api/types';
import { findElement, slabLabel, spaceLabel, type FoundElement, type GeometriModel } from './geometri';
import { isSlabRow, slabKindName } from './slabEdit';
import { applyStairOps, stairFieldOps, stairLabel, stairProperties, type StairFieldKey } from './stairs';
import { hingeFor, readSwing, swingPatch, type SwingSide } from './openingSwing';
import { formatLength } from './ruler';
import { formatAreaDa, polygonArea, spaceAt } from './spaces';
import { expandServerOp } from './wallModify';
import { applyZoneOps } from './zones';

// ------------------------------------------------------------- numbers --

/**
 * A length typed in Danish or English: "0,12", "0.12 m", "12 cm", "120 mm",
 * "1 200" (a thousands space). Metres; null for anything else.
 */
export function parseLength(text: string): number | null {
  const t = text.trim().toLowerCase().replace(/\s+(?=\d)/g, '');
  const m = /^(-?\d+(?:[.,]\d*)?|-?[.,]\d+)\s*(mm|cm|m)?$/.exec(t);
  if (!m) return null;
  const n = Number(m[1].replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  const unit = m[2] ?? 'm';
  return unit === 'mm' ? n / 1000 : unit === 'cm' ? n / 100 : n;
}

/** A length for a text field: "0,12" (metres, two decimals, no unit). */
export function lengthInput(m: number): string {
  const r = Number(m.toFixed(2));
  return (r === 0 ? 0 : r).toFixed(2).replace('.', ',');
}

/** Equal within half a millimetre (the model's own rounding). */
const same = (a: number, b: number) => Math.abs(a - b) < 5e-4;

// ------------------------------------------------------- edited_from --

/** The detected / imported record a row was edited from (`metadata.edited_from`), or null. */
export function editedFrom(metadata: unknown): Record<string, unknown> | null {
  if (typeof metadata !== 'object' || metadata === null) return null;
  const from = (metadata as Record<string, unknown>).edited_from;
  return typeof from === 'object' && from !== null && !Array.isArray(from) ? (from as Record<string, unknown>) : null;
}

const num = (r: Record<string, unknown> | null, key: string): number | null => {
  const v = r?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
};

/** A world point of `edited_from` (ruxd-qfc serves it under the GET names), or null. */
const xy = (r: Record<string, unknown> | null, key: string): [number, number] | null => {
  const v = r?.[key];
  return Array.isArray(v) && v.length >= 2 && typeof v[0] === 'number' && typeof v[1] === 'number' ? [v[0], v[1]] : null;
};

/** The detected axis: world `start` / `end` (ruxd-qfc), else the legacy level-frame `x0…y1` (its length only). */
function fromAxis(from: Record<string, unknown> | null): { start: [number, number]; end: [number, number]; world: boolean } | null {
  const a = xy(from, 'start');
  const b = xy(from, 'end');
  if (a && b) return { start: a, end: b, world: true };
  const k = ['x0', 'y0', 'x1', 'y1'].map((key) => num(from, key));
  return k.every((v) => v !== null) ? { start: [k[0]!, k[1]!], end: [k[2]!, k[3]!], world: false } : null;
}

/** The detected outline of a space (world), or null. */
function fromPolygon(from: Record<string, unknown> | null): { outer: [number, number][]; holes: [number, number][][] } | null {
  const p = from?.polygon as { outer?: unknown; holes?: unknown } | undefined;
  if (!p || !Array.isArray(p.outer)) return null;
  return { outer: p.outer as [number, number][], holes: Array.isArray(p.holes) ? (p.holes as [number, number][][]) : [] };
}

const sameXY = (a: readonly number[], b: readonly number[]) => same(a[0], b[0]) && same(a[1], b[1]);
const sameRing = (a: readonly (readonly number[])[], b: readonly (readonly number[])[]) => a.length === b.length && a.every((p, i) => sameXY(p, b[i]));
const fmtXY = (p: readonly number[]) => `x ${p[0].toFixed(2).replace('.', ',').replace('-', '−')} · y ${p[1].toFixed(2).replace('.', ',').replace('-', '−')}`;
const mid = (a: readonly number[], b: readonly number[]) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];

/** The read-only Placering field: where it stands, edited when it moved from `detected` (display text). */
function positionField(display: string, edited: boolean, detected: string | null): EditField {
  return { key: 'position', label: 'Placering', type: 'text', value: null, display, edited, detected, editable: false };
}

// -------------------------------------------------------------- fields --

export type FieldKey =
  | 'length'
  | 'thickness'
  | 'height'
  | 'base'
  | 'external'
  | 'width'
  | 'opening_height'
  | 'sill'
  | 'opening_kind'
  /** A door's swing (frontend-u0o.5): the side it opens to, its hinge jamb, one leaf or two. */
  | 'swing_side'
  | 'hinge'
  | 'leaves'
  /** Read-only: the type it was placed as ("Enkeltdør 90"). */
  | 'type_name'
  | 'name'
  | 'space_kind'
  /** A space's room in the scan's `rooms` cloud (`room_label`, = Room.id): a whole number, or none. */
  | 'room_label'
  | 'floor'
  | 'ceiling_height'
  /** Where it stands (a wall's axis, an opening's place on its host, a room's outline): shown, reset with ↺. */
  | 'position'
  /** Read-only: shown, never written. */
  | 'area'
  /** A slab row's kind (Gulv / Loft / Tag) and its top above the storey floor (S7). */
  | 'slab_kind'
  | 'slab_top'
  /** Read-only: a slab's holes. */
  | 'holes'
  /** A stair's fields (S8, `stairs.ts`): editable, and the derived ones shown read-only. */
  | StairFieldKey
  | 'stair_riser'
  | 'stair_run'
  | 'stair_comfort'
  | 'stair_from';

export interface FieldOption {
  value: string;
  label: string;
}

/** One field of the Egenskaber panel. */
export interface EditField {
  key: FieldKey;
  label: string;
  /** `length`: a number in metres typed as text; `choice`: a segmented control; `text`: free text. */
  type: 'length' | 'choice' | 'text';
  /** For a length: metres (null = unknown); for a choice: the option value; for text: the text. */
  value: number | string | null;
  /** How the value reads, e.g. "0,12 m". */
  display: string;
  options?: FieldOption[];
  /** Differs from what detection or the import made (accent border). */
  edited: boolean;
  /** The detected value, for ↺'s tooltip ("Detekteret: 0,10 m"); null when not resettable. */
  detected: string | null;
  /** A write can change it (false: shown read-only). */
  editable: boolean;
  /** What an empty text field says ("Intet"). */
  placeholder?: string;
}

export interface PropertySection {
  title: string;
  fields: EditField[];
}

export type ChipTone = 'accent' | 'neutral' | 'success' | 'warning';

export interface PropertiesView {
  guid: string;
  kind: FoundElement['kind'];
  /** "Væg · Inderæg", "Åbning · Dør". */
  eyebrow: string;
  title: string;
  chips: { label: string; tone: ChipTone }[];
  sections: PropertySection[];
  /** Tilbage til detekteret applies (the row was edited from a detected or imported one, or drawn by hand). */
  revertable: boolean;
  /** Slet applies (slabs are derived, never deleted). */
  deletable: boolean;
  related: { guid: string; label: string; role: string }[];
}

const wallLength = (w: Pick<SolidWall, 'start' | 'end'>) => Math.hypot(w.end[0] - w.start[0], w.end[1] - w.start[1]);

function storeyOf(model: GeometriModel, id: number | null): Storey | null {
  return id === null ? null : (model.storeys?.find((s) => s.id === id) ?? null);
}

const OPENING_KINDS: FieldOption[] = [
  { value: 'door', label: 'Dør' },
  { value: 'window', label: 'Vindue' },
  { value: 'opening', label: 'Hul' },
];
const LEAVES: FieldOption[] = [
  { value: '1', label: 'Enkelt' },
  { value: '2', label: 'Dobbelt' },
];

/** The hinge as it reads from the swing side: Venstre / Højre. */
function hingeOptions(side: SwingSide): FieldOption[] {
  return [
    { value: hingeFor(side, 'V'), label: 'Venstre' },
    { value: hingeFor(side, 'H'), label: 'Højre' },
  ];
}

/**
 * A door's two sides named by what lies there: "Mod Stue" (the room a step
 * off the wall), "Mod ude" (an external wall's outside), else "Side A / B"
 * (a is the host's left of start → end).
 */
function swingSides(model: GeometriModel, o: SolidOpening, host: SolidWall): FieldOption[] {
  const L = wallLength(host);
  const name = (side: SwingSide): string => {
    if (L > 1e-9 && o.start && o.end) {
      const k = (side === 'a' ? 1 : -1) * ((host.thickness ?? 0) / 2 + 0.4);
      const n = [-(host.end[1] - host.start[1]) / L, (host.end[0] - host.start[0]) / L];
      const c = [(o.start[0] + o.end[0]) / 2 + n[0] * k, (o.start[1] + o.end[1]) / 2 + n[1] * k];
      const rooms = (model.spaces ?? []).filter((s) => host.storey_id === null || s.storey_id === host.storey_id);
      const room = spaceAt(rooms, c[0], c[1]);
      if (room) return `Mod ${spaceLabel(room)}`;
      if (host.is_external) {
        const other = spaceAt(rooms, c[0] - n[0] * 2 * k, c[1] - n[1] * 2 * k);
        if (other) return 'Mod ude';
      }
    }
    return side === 'a' ? 'Side A' : 'Side B';
  };
  const a = name('a');
  const b = name('b');
  // Two sides that read the same (one room both ways) fall back to A / B.
  return a === b ? [{ value: 'a', label: 'Side A' }, { value: 'b', label: 'Side B' }] : [{ value: 'a', label: a }, { value: 'b', label: b }];
}

const SPACE_KINDS: FieldOption[] = [
  { value: 'room', label: 'Rum' },
  { value: 'corridor', label: 'Gang' },
  { value: 'open_plan', label: 'Åben' },
];
const SLAB_KINDS: FieldOption[] = [
  { value: 'floor', label: 'Gulv' },
  { value: 'ceiling', label: 'Loft' },
  { value: 'roof', label: 'Tag' },
];
const WALL_TYPES: FieldOption[] = [
  { value: 'inner', label: 'Inder' },
  { value: 'outer', label: 'Yder' },
];

function lengthField(key: FieldKey, label: string, value: number | null, detected: number | null, editable = true): EditField {
  return {
    key,
    label,
    type: 'length',
    value,
    display: value === null ? 'ukendt' : formatLength(value),
    edited: detected !== null && (value === null || !same(value, detected)),
    detected: detected === null ? null : formatLength(detected),
    editable,
  };
}

function choiceField(key: FieldKey, label: string, options: FieldOption[], value: string, detected: string | null): EditField {
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  return {
    key,
    label,
    type: 'choice',
    value,
    display: labelOf(value),
    options,
    edited: detected !== null && detected !== value,
    detected: detected === null ? null : labelOf(detected),
    editable: true,
  };
}

/** The fields of a wall, an opening or a space; null for a slab (derived, not edited in S2). */
export function editFields(model: GeometriModel, found: FoundElement): PropertySection[] {
  if (found.kind === 'stair') return stairProperties(model, found.row).sections;
  switch (found.kind) {
    case 'wall': {
      const w = found.row;
      const from = editedFrom(w.metadata);
      const floor = storeyOf(model, w.storey_id)?.z_min ?? 0;
      const axis = fromAxis(from);
      const fromLen = axis ? Math.hypot(axis.end[0] - axis.start[0], axis.end[1] - axis.start[1]) : null;
      const fromThick = num(from, 'thickness');
      // World z_base / z_top (ruxd-qfc); the legacy record has only a height.
      const fromBase = axis?.world ? num(from, 'z_base') : null;
      const fromTop = num(from, 'z_top');
      const fromHeight = fromBase !== null && fromTop !== null ? fromTop - fromBase : num(from, 'height');
      const moved = axis?.world ? !(sameXY(axis.start, w.start) && sameXY(axis.end, w.end)) : false;
      const fromExternal = typeof from?.is_external === 'boolean' ? (from.is_external ? 'outer' : 'inner') : null;
      return [
        {
          title: 'Geometri',
          fields: [
            lengthField('length', 'Længde', wallLength(w), fromLen),
            // A thickness of −1 in the record is "unknown".
            lengthField('thickness', 'Tykkelse', w.thickness, fromThick !== null && fromThick > 0 ? fromThick : null),
            lengthField('height', 'Højde', w.z_top - w.z_base, fromHeight),
            lengthField('base', 'Underkant', w.z_base - floor, fromBase !== null ? fromBase - floor : null),
            positionField(fmtXY(mid(w.start, w.end)), moved, axis?.world ? fmtXY(mid(axis.start, axis.end)) : null),
          ],
        },
        { title: 'Rolle', fields: [choiceField('external', 'Type', WALL_TYPES, w.is_external ? 'outer' : 'inner', fromExternal)] },
      ];
    }
    case 'opening': {
      const o = found.row;
      const from = editedFrom(o.metadata);
      const host = model.walls?.find((w) => w.guid === o.wall_guid) ?? null;
      const s0 = num(from, 's_start');
      const s1 = num(from, 's_end');
      const zs = num(from, 'z_sill');
      const zh = num(from, 'z_head');
      const fromKind = typeof from?.kind === 'string' ? from.kind : null;
      const base = host?.z_base ?? storeyOf(model, o.storey_id)?.z_min ?? 0;
      const lo = Math.min(o.s_start, o.s_end);
      const fromLo = s0 !== null && s1 !== null ? Math.min(s0, s1) : null;
      const sw = readSwing(o);
      // The detected leaf (`edited_from` carries the wire's fields); unknown there is no value to reset to.
      const fromSwing = from ? readSwing({ metadata: null, ...(from as Partial<SolidOpening>) }) : null;
      return [
        {
          title: 'Mål',
          fields: [
            lengthField('width', 'Bredde', Math.abs(o.s_end - o.s_start), s0 !== null && s1 !== null ? Math.abs(s1 - s0) : null),
            lengthField('opening_height', 'Højde', o.z_head - o.z_sill, zs !== null && zh !== null ? zh - zs : null),
            lengthField('sill', 'Brystning', o.z_sill - base, zs !== null ? zs - base : null),
            positionField(`${formatLength(lo)} fra vægstart`, fromLo !== null && !same(fromLo, lo), fromLo !== null ? `${formatLength(fromLo)} fra vægstart` : null),
          ],
        },
        {
          title: 'Type',
          fields: [
            choiceField('opening_kind', 'Art', OPENING_KINDS, o.kind, fromKind),
            ...(sw.typeName ? [{ key: 'type_name' as const, label: 'Type', type: 'text' as const, value: null, display: sw.typeName, edited: false, detected: null, editable: false }] : []),
          ],
        },
        ...(o.kind === 'door' && host
          ? [
              {
                title: 'Slag',
                fields: [
                  choiceField('swing_side', 'Slår', swingSides(model, o, host), sw.side, fromSwing?.known && (from?.swing_side === 'a' || from?.swing_side === 'b') ? fromSwing.side : null),
                  ...(sw.leaves === 1 ? [choiceField('hinge', 'Hængsel', hingeOptions(sw.side), sw.hinge, fromSwing?.known && (from?.hinge === 'start' || from?.hinge === 'end') ? fromSwing.hinge : null)] : []),
                  choiceField('leaves', 'Fløje', LEAVES, String(sw.leaves), from?.operation === 'single_swing' || from?.operation === 'double_swing' ? String(fromSwing!.leaves) : null),
                ],
              },
            ]
          : []),
      ];
    }
    case 'space': {
      const s = found.row;
      const from = editedFrom(s.metadata);
      const zf = num(from, 'z_floor');
      const zc = num(from, 'z_ceiling');
      const floor = storeyOf(model, s.storey_id)?.z_min ?? 0;
      const fromName = typeof from?.name === 'string' ? from.name : null;
      const fromKind = typeof from?.kind === 'string' ? from.kind : null;
      const kindOptions = SPACE_KINDS.some((k) => k.value === s.kind) ? SPACE_KINDS : [...SPACE_KINDS, { value: s.kind, label: 'Ukendt' }];
      return [
        {
          title: 'Rum',
          fields: [
            {
              key: 'name',
              label: 'Navn',
              type: 'text',
              value: s.name,
              display: spaceLabel(s),
              edited: fromName !== null && fromName !== s.name,
              detected: fromName,
              editable: true,
            },
            choiceField('space_kind', 'Type', kindOptions, s.kind, fromKind),
            (() => {
              const fromLabel = from && 'room_label' in from ? (typeof from.room_label === 'number' ? from.room_label : null) : undefined;
              return {
                key: 'room_label' as const,
                label: 'Scanningsrum',
                type: 'text' as const,
                value: s.room_label === null ? '' : String(s.room_label),
                display: s.room_label === null ? 'Intet' : `Nr. ${s.room_label}`,
                edited: fromLabel !== undefined && fromLabel !== s.room_label,
                detected: fromLabel === undefined ? null : fromLabel === null ? 'Intet' : `Nr. ${fromLabel}`,
                editable: true,
                placeholder: 'Intet',
              };
            })(),
            ...(typeof s.area === 'number'
              ? [{ key: 'area' as const, label: 'Areal', type: 'text' as const, value: null, display: formatAreaDa(s.area), edited: false, detected: null, editable: false }]
              : []),
          ],
        },
        {
          title: 'Højder',
          fields: [
            lengthField('floor', 'Gulv', s.z_floor - floor, zf !== null ? zf - floor : null),
            lengthField('ceiling_height', 'Loftshøjde', s.z_ceiling - s.z_floor, zf !== null && zc !== null ? zc - zf : null),
          ],
        },
        ...(() => {
          const poly = fromPolygon(from);
          const centre = (ring: readonly (readonly number[])[]) => ring.reduce((a, p) => [a[0] + p[0] / ring.length, a[1] + p[1] / ring.length], [0, 0]);
          if (!poly && s.polygon.outer.length === 0) return [];
          const moved = poly ? !sameRing(poly.outer, s.polygon.outer) : false;
          return [{ title: 'Placering', fields: [positionField(fmtXY(centre(s.polygon.outer)), moved, poly ? fmtXY(centre(poly.outer)) : null)] }];
        })(),
      ];
    }
    case 'slab': {
      const sl = found.row;
      const floor = storeyOf(model, sl.storey_id)?.z_min ?? 0;
      const area: EditField = { key: 'area', label: 'Areal', type: 'text', value: null, display: formatAreaDa(sl.area), edited: false, detected: null, editable: false };
      const holes: EditField[] =
        sl.polygon.holes.length > 0
          ? [{ key: 'holes', label: 'Huller', type: 'text', value: null, display: String(sl.polygon.holes.length), edited: false, detected: null, editable: false }]
          : [];
      if (!isSlabRow(sl)) return [{ title: 'Mål', fields: [lengthField('thickness', 'Tykkelse', sl.z_top - sl.z_base, null, false), area, ...holes] }];
      const from = editedFrom(sl.metadata);
      const fromTop = num(from, 'z_top');
      const fromBase = num(from, 'z_base');
      const fromKind = typeof from?.kind === 'string' ? from.kind : null;
      return [
        {
          title: 'Dæk',
          fields: [
            choiceField('slab_kind', 'Art', SLAB_KINDS, sl.kind, fromKind),
            lengthField('slab_top', 'Overkant', sl.z_top - floor, fromTop !== null ? fromTop - floor : null),
            lengthField('thickness', 'Tykkelse', sl.z_top - sl.z_base, fromTop !== null && fromBase !== null ? fromTop - fromBase : null),
            area,
            ...holes,
          ],
        },
      ];
    }
  }
}

// ----------------------------------------------------------------- ops --

/**
 * The ops that set field `key` of element `guid` to `value` (metres for a
 * length, the option value for a choice, the text for a name). Null when
 * the value is not valid there (a negative thickness, an empty name, a wall
 * of no length) or nothing would change.
 */
export function fieldOps(model: GeometriModel, guid: string, key: FieldKey, value: number | string): ModelEditOp[] | null {
  const found = findElement(model, guid);
  if (!found) return null;
  if (found.kind === 'stair') return stairFieldOps(model, found.row, key, value);
  const n = typeof value === 'number' ? value : NaN;
  switch (found.kind) {
    case 'wall': {
      const w = found.row;
      const floor = storeyOf(model, w.storey_id)?.z_min ?? 0;
      switch (key) {
        case 'length': {
          const L = wallLength(w);
          if (!(n > 0.01) || !(L > 1e-6) || same(n, L)) return null;
          const d = [(w.end[0] - w.start[0]) / L, (w.end[1] - w.start[1]) / L];
          return [{ op: 'wall.update', guid, set: { end: [round(w.start[0] + d[0] * n), round(w.start[1] + d[1] * n)] } }];
        }
        case 'thickness':
          if (!(n > 0.005) || (w.thickness !== null && same(n, w.thickness))) return null;
          return [{ op: 'wall.update', guid, set: { thickness: round(n), assumed: false } }];
        case 'height':
          if (!(n > 0.05) || same(n, w.z_top - w.z_base)) return null;
          return [{ op: 'wall.update', guid, set: { z_top: round(w.z_base + n) } }];
        case 'base': {
          if (!Number.isFinite(n) || same(n, w.z_base - floor)) return null;
          const zBase = floor + n;
          return [{ op: 'wall.update', guid, set: { z_base: round(zBase), z_top: round(zBase + (w.z_top - w.z_base)) } }];
        }
        case 'external': {
          const ext = value === 'outer';
          if (ext === w.is_external) return null;
          return [{ op: 'wall.update', guid, set: { is_external: ext } }];
        }
        default:
          return null;
      }
    }
    case 'opening': {
      const o = found.row;
      const host = model.walls?.find((w) => w.guid === o.wall_guid) ?? null;
      switch (key) {
        case 'width': {
          const lo = Math.min(o.s_start, o.s_end);
          const hi = Math.max(o.s_start, o.s_end);
          if (!(n > 0.05) || same(n, hi - lo)) return null;
          // Widened about its middle, kept on the host.
          const mid = (lo + hi) / 2;
          let a = mid - n / 2;
          let b = mid + n / 2;
          const L = host ? wallLength(host) : Infinity;
          if (a < 0) [a, b] = [0, n];
          if (b > L) [a, b] = [Math.max(0, L - n), L];
          return [{ op: 'opening.update', guid, set: { s_start: round(a), s_end: round(b) } }];
        }
        case 'opening_height':
          if (!(n > 0.05) || same(n, o.z_head - o.z_sill)) return null;
          return [{ op: 'opening.update', guid, set: { z_head: round(o.z_sill + n) } }];
        case 'sill': {
          const base = host?.z_base ?? storeyOf(model, o.storey_id)?.z_min ?? 0;
          if (!(n >= 0) || same(n, o.z_sill - base)) return null;
          const h = o.z_head - o.z_sill;
          return [{ op: 'opening.update', guid, set: { z_sill: round(base + n), z_head: round(base + n + h) } }];
        }
        case 'opening_kind':
          if (value === o.kind || !OPENING_KINDS.some((k) => k.value === value)) return null;
          return [{ op: 'opening.update', guid, set: { kind: value } }];
        case 'swing_side':
        case 'hinge':
        case 'leaves': {
          if (o.kind !== 'door') return null;
          const sw = readSwing(o);
          if (key === 'swing_side') return (value === 'a' || value === 'b') && value !== sw.side ? [{ op: 'opening.update', guid, set: swingPatch(o, { side: value }) }] : null;
          if (key === 'hinge') return (value === 'start' || value === 'end') && value !== sw.hinge ? [{ op: 'opening.update', guid, set: swingPatch(o, { hinge: value }) }] : null;
          const leaves = value === '2' ? 2 : value === '1' ? 1 : null;
          return leaves !== null && leaves !== sw.leaves ? [{ op: 'opening.update', guid, set: swingPatch(o, { leaves }) }] : null;
        }
        default:
          return null;
      }
    }
    case 'space': {
      const s = found.row;
      const floor = storeyOf(model, s.storey_id)?.z_min ?? 0;
      switch (key) {
        case 'name': {
          const name = typeof value === 'string' ? value.trim() : '';
          if (name === '' || name === s.name) return null;
          return [{ op: 'space.update', guid, set: { name } }];
        }
        case 'space_kind':
          if (value === s.kind) return null;
          return [{ op: 'space.update', guid, set: { kind: value } }];
        case 'room_label': {
          const t = String(value).trim();
          const label = t === '' ? null : /^\d+$/.test(t) ? Number(t) : undefined;
          if (label === undefined || label === s.room_label) return null;
          return [{ op: 'space.update', guid, set: { room_label: label } }];
        }
        case 'floor': {
          if (!Number.isFinite(n) || same(n, s.z_floor - floor)) return null;
          const zf = floor + n;
          return [{ op: 'space.update', guid, set: { z_floor: round(zf), z_ceiling: round(zf + (s.z_ceiling - s.z_floor)) } }];
        }
        case 'ceiling_height':
          if (!(n > 0.5) || same(n, s.z_ceiling - s.z_floor)) return null;
          return [{ op: 'space.update', guid, set: { z_ceiling: round(s.z_floor + n) } }];
        default:
          return null;
      }
    }
    case 'slab': {
      const sl = found.row;
      if (!isSlabRow(sl)) return null;
      const floor = storeyOf(model, sl.storey_id)?.z_min ?? 0;
      switch (key) {
        case 'slab_kind':
          if (value === sl.kind || !SLAB_KINDS.some((k) => k.value === value)) return null;
          return [{ op: 'slab.update', guid, set: { kind: value } }];
        case 'slab_top': {
          if (!Number.isFinite(n) || same(n, sl.z_top - floor)) return null;
          const top = floor + n;
          return [{ op: 'slab.update', guid, set: { z_top: round(top), z_base: round(top - (sl.z_top - sl.z_base)) } }];
        }
        case 'thickness':
          if (!(n > 0.005) || same(n, sl.z_top - sl.z_base)) return null;
          return [{ op: 'slab.update', guid, set: { z_base: round(sl.z_top - n) } }];
        default:
          return null;
      }
    }
  }
}

/**
 * ↺ on field `key`: the ops that put its detected value (from
 * `edited_from`) back, leaving every other field as edited. Null when the
 * field has no detected value or already holds it.
 */
export function resetFieldOps(model: GeometriModel, guid: string, key: FieldKey): ModelEditOp[] | null {
  const found = findElement(model, guid);
  if (!found || found.kind === 'stair') return null;
  const from = editedFrom(found.row.metadata);
  if (!from) return null;
  if (found.kind === 'slab') {
    const sl = found.row;
    const floor = storeyOf(model, sl.storey_id)?.z_min ?? 0;
    const top = num(from, 'z_top');
    const bottom = num(from, 'z_base');
    if (key === 'slab_kind') return typeof from.kind === 'string' ? fieldOps(model, guid, key, from.kind) : null;
    if (key === 'slab_top') return top !== null ? fieldOps(model, guid, key, top - floor) : null;
    if (key === 'thickness') return top !== null && bottom !== null ? fieldOps(model, guid, key, top - bottom) : null;
    return null;
  }
  const field = editFields(model, found)
    .flatMap((s) => s.fields)
    .find((f) => f.key === key);
  if (!field || !field.edited) return null;
  switch (key) {
    case 'length': {
      const L = Math.hypot(num(from, 'x1')! - num(from, 'x0')!, num(from, 'y1')! - num(from, 'y0')!);
      return fieldOps(model, guid, key, L);
    }
    case 'thickness':
      return fieldOps(model, guid, key, num(from, 'thickness')!);
    case 'height':
      return fieldOps(model, guid, key, num(from, 'height')!);
    case 'external':
      return fieldOps(model, guid, key, from.is_external ? 'outer' : 'inner');
    case 'width':
      return fieldOps(model, guid, key, Math.abs(num(from, 's_end')! - num(from, 's_start')!));
    case 'opening_height':
      return fieldOps(model, guid, key, num(from, 'z_head')! - num(from, 'z_sill')!);
    case 'swing_side':
      return typeof from.swing_side === 'string' ? fieldOps(model, guid, key, from.swing_side) : null;
    case 'hinge':
      return typeof from.hinge === 'string' ? fieldOps(model, guid, key, from.hinge) : null;
    case 'leaves':
      return fieldOps(model, guid, key, from.operation === 'double_swing' ? '2' : '1');
    case 'opening_kind':
    case 'space_kind':
      return typeof from.kind === 'string' ? fieldOps(model, guid, key, from.kind) : null;
    case 'name':
      return typeof from.name === 'string' ? fieldOps(model, guid, key, from.name) : null;
    case 'room_label':
      return fieldOps(model, guid, key, typeof from.room_label === 'number' ? String(from.room_label) : '');
    case 'ceiling_height':
      return fieldOps(model, guid, key, num(from, 'z_ceiling')! - num(from, 'z_floor')!);
    case 'base': {
      // The whole wall back to its detected heights.
      const zb = num(from, 'z_base');
      const zt = num(from, 'z_top');
      if (zb === null) return null;
      const w = found.row as SolidWall;
      return [{ op: 'wall.update', guid, set: { z_base: round(zb), z_top: round(zt ?? zb + (w.z_top - w.z_base)) } }];
    }
    case 'sill': {
      const o = found.row as SolidOpening;
      const zs = num(from, 'z_sill');
      return zs === null ? null : [{ op: 'opening.update', guid, set: { z_sill: round(zs), z_head: round(zs + (o.z_head - o.z_sill)) } }];
    }
    case 'floor': {
      const sp = found.row as SolidSpace;
      const zf = num(from, 'z_floor');
      return zf === null ? null : [{ op: 'space.update', guid, set: { z_floor: round(zf), z_ceiling: round(zf + (sp.z_ceiling - sp.z_floor)) } }];
    }
    case 'position': {
      if (found.kind === 'wall') {
        const axis = fromAxis(from);
        return axis?.world ? [{ op: 'wall.update', guid, set: { start: axis.start, end: axis.end } }] : null;
      }
      if (found.kind === 'opening') {
        const s0 = num(from, 's_start');
        const s1 = num(from, 's_end');
        return s0 !== null && s1 !== null ? [{ op: 'opening.update', guid, set: { s_start: s0, s_end: s1 } }] : null;
      }
      const poly = fromPolygon(from);
      return poly ? [{ op: 'space.update', guid, set: { polygon: poly } }] : null;
    }
    default:
      return null;
  }
}

/** Ops for Slet: one delete per wall, opening or space (slabs are derived and skipped). Openings on a deleted wall go too. */
export function deleteOps(model: GeometriModel, guids: readonly string[]): ModelEditOp[] {
  const out: ModelEditOp[] = [];
  const seen = new Set<string>();
  const add = (op: ModelEditOp & { guid: string }) => {
    if (seen.has(op.guid)) return;
    seen.add(op.guid);
    out.push(op);
  };
  // Openings first: a wall's delete would otherwise leave them hostless for a moment.
  for (const g of guids) {
    const f = findElement(model, g);
    if (f?.kind === 'wall') for (const o of model.openings ?? []) if (o.wall_guid === g) add({ op: 'opening.delete', guid: o.guid });
  }
  for (const g of guids) {
    const f = findElement(model, g);
    if (!f || (f.kind === 'slab' && !isSlabRow(f.row))) continue;
    add({ op: `${f.kind}.delete`, guid: g });
  }
  return out;
}

/** Whether "Tilbage til detekteret" means anything for the row: edited, or drawn by hand. */
export function isRevertable(row: { source?: string; edited?: boolean; metadata?: unknown }): boolean {
  return row.edited === true || editedFrom(row.metadata) !== null || row.source === 'manual';
}

/** Ops for Tilbage til detekteret over a selection (only the rows it applies to). */
export function revertOps(model: GeometriModel, guids: readonly string[]): ModelEditOp[] {
  const out: ModelEditOp[] = [];
  for (const g of guids) {
    const f = findElement(model, g);
    if (!f || (f.kind === 'slab' && !isSlabRow(f.row)) || !isRevertable(f.row)) continue;
    out.push({ op: `${f.kind}.revert`, guid: g });
  }
  return out;
}

/** The history label of a batch ("Tykkelse · Væg 6", "Slet 3 elementer"). */
export function editLabel(action: string, model: GeometriModel, guids: readonly string[]): string {
  if (guids.length !== 1) return `${action} ${guids.length} elementer`;
  const f = findElement(model, guids[0]);
  return f ? `${action} · ${elementTitle(f)}` : action;
}

// --------------------------------------------------- properties panel --

const SOURCE_LABEL: Record<string, string> = { detected: 'Detekteret', manual: 'Manuel', imported: 'Importeret' };
const OPENING_TITLE: Record<SolidOpening['kind'], string> = { door: 'Dør', window: 'Vindue', opening: 'Åbning' };

/** "Væg 6", "Dør D3", "Køkken". */
export function elementTitle(f: FoundElement): string {
  if (f.kind === 'stair') return stairLabel(f.row);
  switch (f.kind) {
    case 'wall':
      return `Væg ${f.row.id}`;
    case 'opening':
      return `${OPENING_TITLE[f.row.kind]} ${f.row.kind === 'door' ? 'D' : f.row.kind === 'window' ? 'V' : 'Å'}${f.row.id}`;
    case 'space':
      return spaceLabel(f.row);
    case 'slab':
      return slabLabel(f.row);
  }
}

/** The floating Egenskaber panel for one selected element; null when nothing (known) is selected. */
export function propertiesView(model: GeometriModel, guid: string | null): PropertiesView | null {
  const found = findElement(model, guid);
  if (!found) return null;
  if (found.kind === 'stair') return stairProperties(model, found.row);
  const title = elementTitle(found);
  if (found.kind === 'slab') {
    const s = found.row;
    const name = slabKindName(s.kind);
    if (!isSlabRow(s)) {
      return {
        guid: s.guid,
        kind: 'slab',
        eyebrow: `${name} · afledt`,
        title,
        chips: [{ label: 'Afledt — genberegn dæk og tag for at redigere', tone: 'neutral' }],
        sections: editFields(model, found),
        revertable: false,
        deletable: false,
        related: [],
      };
    }
    const edited = s.edited === true || editedFrom(s.metadata) !== null;
    const chips: PropertiesView['chips'] = [{ label: edited ? 'Redigeret' : (SOURCE_LABEL[s.source ?? ''] ?? 'Afledt'), tone: edited ? 'accent' : 'neutral' }];
    if (s.locked) chips.push({ label: 'Låst', tone: 'neutral' });
    if (s.built === false) chips.push({ label: 'Ikke genberegnet', tone: 'warning' });
    return {
      guid: s.guid,
      kind: 'slab',
      eyebrow: name,
      title,
      chips,
      sections: editFields(model, found),
      revertable: isRevertable(s),
      deletable: true,
      related: [],
    };
  }
  const row = found.row as SolidWall | SolidOpening | SolidSpace;
  const edited = row.edited === true || editedFrom(row.metadata) !== null;
  const chips: PropertiesView['chips'] = [{ label: edited ? 'Redigeret' : (SOURCE_LABEL[row.source] ?? row.source), tone: edited ? 'accent' : 'neutral' }];
  if (row.locked) chips.push({ label: 'Låst', tone: 'neutral' });
  if (row.confidence !== null) chips.push({ label: `Sikkerhed ${Math.round(row.confidence * 100)} %`, tone: row.confidence < 0.6 ? 'warning' : 'success' });
  if (found.kind === 'wall' && found.row.assumed) chips.push({ label: 'Tykkelse antaget', tone: 'warning' });

  let eyebrow = '';
  let related: PropertiesView['related'] = [];
  switch (found.kind) {
    case 'wall':
      eyebrow = `Væg · ${found.row.is_external ? 'Ydervæg' : 'Indervæg'}`;
      related = (model.openings ?? [])
        .filter((o) => o.wall_guid === found.row.guid)
        .map((o) => ({ guid: o.guid, label: elementTitle({ kind: 'opening', row: o }), role: 'Åbning' }));
      break;
    case 'opening': {
      eyebrow = `Åbning · ${OPENING_TITLE[found.row.kind]}`;
      const host = model.walls?.find((w) => w.guid === found.row.wall_guid);
      related = host ? [{ guid: host.guid, label: `Væg ${host.id}`, role: 'Vært' }] : [];
      break;
    }
    case 'space':
      eyebrow = found.row.kind === 'corridor' ? 'Rum · Gang' : 'Rum';
      break;
  }
  return {
    guid: row.guid,
    kind: found.kind,
    eyebrow,
    title,
    chips,
    sections: editFields(model, found),
    revertable: isRevertable(row),
    deletable: true,
    related,
  };
}

// -------------------------------------------------- optimistic overlay --

const round = (v: number) => Math.round(v * 1e6) / 1e6;

type Row = SolidWall | SolidOpening | SolidSpace | Storey;

function markEdited<T extends Row>(row: T): T {
  return { ...row, source: 'manual', locked: true, edited: true };
}

/** Where an opening's `s` lands on its host's axis (world x/y), as the GET serves `start` / `end`. */
function onAxis(host: SolidWall | undefined, s: number): [number, number] | null {
  if (!host) return null;
  const L = wallLength(host);
  if (!(L > 1e-9)) return null;
  const t = s / L;
  return [round(host.start[0] + (host.end[0] - host.start[0]) * t), round(host.start[1] + (host.end[1] - host.start[1]) * t)];
}

/** A room re-measured as the server does when its outline or heights change: area, perimeter, volume. */
function measureSpace(sp: SolidSpace): SolidSpace {
  const outer = sp.polygon?.outer ?? [];
  if (outer.length < 3) return sp;
  const holes = sp.polygon.holes ?? [];
  const area = polygonArea({ outer, holes });
  let perimeter = 0;
  for (let i = 0, j = outer.length - 1; i < outer.length; j = i, i += 1) perimeter += Math.hypot(outer[i][0] - outer[j][0], outer[i][1] - outer[j][1]);
  return { ...sp, polygon: { outer, holes }, area: round(area), perimeter: round(perimeter), volume: round(area * (sp.z_ceiling - sp.z_floor)) };
}

/** A slab row as the server keeps it after an edit: manual and locked, its thickness and area following the faces and outline. */
function patchSlab(sl: SolidSlab, set: Record<string, unknown>): SolidSlab {
  const thick = sl.z_top - sl.z_base;
  const next = { ...sl, ...(set as Partial<SolidSlab>), source: 'manual', locked: true, edited: true, built: false } as SolidSlab;
  if (!('z_base' in set)) {
    const t = typeof set.thickness === 'number' && set.thickness > 0 ? set.thickness : thick;
    next.z_base = round(next.z_top - t);
  }
  next.thickness = round(next.z_top - next.z_base);
  if ('polygon' in set) next.area = round(polygonArea({ outer: next.polygon.outer, holes: next.polygon.holes ?? [] }));
  next.volume = round(next.area * next.thickness);
  return next;
}

function patchWall(w: SolidWall, set: Record<string, unknown>): SolidWall {
  const next = markEdited({ ...w, ...(set as Partial<SolidWall>) });
  if ('z_top' in set || 'z_base' in set) next.height = round(next.z_top - next.z_base);
  else if ('height' in set) next.z_top = round(next.z_base + next.height);
  return next;
}

/**
 * The model as it will be once `ops` are applied — what the plan, the tree
 * and Egenskaber show while the batch is on its way (and until the re-read
 * lands). Updates and deletes are applied; a create is added as sent; a
 * revert, split, merge or join waits for the server (its result is the
 * server's to compute).
 */
export function applyOps(model: GeometriModel, input: readonly ModelEditOp[]): GeometriModel {
  if (input.length === 0) return model;
  // The server's CAD ops, as the update / create ops they amount to (an approximation; the re-read wins).
  const ops: ModelEditOp[] = [];
  let at = model;
  for (const op of input) {
    const expanded = expandServerOp(at, op);
    if (expanded === null) ops.push(op);
    else if ('ops' in expanded) {
      ops.push(...expanded.ops);
      at = applyOps(at, expanded.ops);
    }
  }
  let walls = model.walls ? [...model.walls] : null;
  let openings = model.openings ? [...model.openings] : null;
  let spaces = model.spaces ? [...model.spaces] : null;
  let storeys = model.storeys ? [...model.storeys] : null;
  let slabs = model.slabs ? [...model.slabs] : null;
  const touchedHosts = new Set<string>();
  for (const op of ops) {
    const [kind, verb] = op.op.split('.') as [string, string];
    if (op.op === 'storey.merge') {
      // a spans both and takes the lower idx; b goes (its elements' renumbering is the server's: the re-read wins).
      const [ga, gb] = op.guids;
      const a = storeys?.find((x) => x.guid === ga);
      const b = storeys?.find((x) => x.guid === gb);
      if (storeys && a && b) {
        const merged = markEdited({ ...a, id: Math.min(a.id, b.id), z_min: Math.min(a.z_min, b.z_min), z_max: Math.max(a.z_max, b.z_max) });
        storeys = storeys.filter((x) => x.guid !== gb).map((x) => (x.guid === ga ? merged : x));
      }
      continue;
    }
    if (op.op === 'ceiling.set') {
      if (spaces) spaces = spaces.map((s) => (s.guid === op.guid ? markEdited({ ...s, ceiling_planes: op.planes.map((p) => ({ at: p.at, slope: p.slope ?? [0, 0] })) }) : s));
      continue;
    }
    if (verb === 'update' && 'set' in op) {
      if (kind === 'wall' && walls) {
        walls = walls.map((w) => (w.guid === op.guid ? patchWall(w, op.set) : w));
        touchedHosts.add(op.guid);
      } else if (kind === 'opening' && openings) openings = openings.map((o) => (o.guid === op.guid ? markEdited({ ...o, ...(op.set as Partial<SolidOpening>) }) : o));
      else if (kind === 'space' && spaces) {
        const geo = 'polygon' in op.set || 'z_floor' in op.set || 'z_ceiling' in op.set;
        spaces = spaces.map((s) => (s.guid === op.guid ? markEdited(geo ? measureSpace({ ...s, ...(op.set as Partial<SolidSpace>) }) : { ...s, ...(op.set as Partial<SolidSpace>) }) : s));
      }
      else if (kind === 'storey' && storeys) {
        // One ground storey: marking one clears the mark on the others (ruxd #34).
        const ground = op.set.is_ground === true;
        storeys = storeys.map((s) => (s.guid === op.guid ? markEdited({ ...s, ...(op.set as Partial<Storey>) }) : ground && s.is_ground ? { ...s, is_ground: false } : s));
      } else if (kind === 'slab' && slabs) slabs = slabs.map((s) => (s.guid === op.guid ? patchSlab(s, op.set) : s));
    } else if (verb === 'delete' && 'guid' in op) {
      if (kind === 'wall' && walls) walls = walls.filter((w) => w.guid !== op.guid);
      else if (kind === 'opening' && openings) openings = openings.filter((o) => o.guid !== op.guid);
      else if (kind === 'space' && spaces) spaces = spaces.filter((s) => s.guid !== op.guid);
      else if (kind === 'storey' && storeys) storeys = storeys.filter((s) => s.guid !== op.guid);
      else if (kind === 'slab' && slabs) slabs = slabs.filter((s) => s.guid !== op.guid);
    } else if (verb === 'create' && 'record' in op && typeof op.record.guid === 'string') {
      const rec = { source: 'manual', locked: true, edited: true, confidence: null, metadata: null, ...op.record } as unknown;
      const guid = op.record.guid;
      // The re-read may hold the row already (the batch stays on top until it lands): replace, never twice.
      const put = <T extends { guid: string }>(rows: T[], row: T) => [...rows.filter((r) => r.guid !== guid), row];
      if (kind === 'wall' && walls) walls = put(walls, rec as SolidWall);
      else if (kind === 'opening' && openings) openings = put(openings, rec as SolidOpening);
      else if (kind === 'space' && spaces) spaces = put(spaces, measureSpace({ area: null, perimeter: null, volume: null, room_label: null, storey_guid: null, ...(rec as object) } as SolidSpace));
      else if (kind === 'storey' && storeys) {
        const r = op.record as Record<string, unknown>;
        const row = { room_ids: [], is_ground: false, ...(rec as object), id: typeof r.idx === 'number' ? r.idx : -1 } as unknown as Storey;
        storeys = put(storeys, row);
      } else if (kind === 'slab' && slabs) {
        const blank = { id: -1, storey_guid: null, part: null, polygon: { outer: [], holes: [] }, z_base: 0, z_top: 0, area: 0, volume: 0, closed: false, built: false, top_planes: [], kind: 'floor' } as unknown as SolidSlab;
        slabs = put(slabs, patchSlab(blank, op.record as Record<string, unknown>));
      }
    }
  }
  // Openings follow their host's axis (the GET's derived `start` / `end`).
  if (openings && walls) {
    const byGuid = new Map(walls.map((w) => [w.guid, w]));
    const named = (op: ModelEditOp, guid: string) => ('guid' in op && op.guid === guid) || ('record' in op && op.record.guid === guid);
    openings = openings.map((o) => {
      if (!touchedHosts.has(o.wall_guid) && !ops.some((op) => named(op, o.guid))) return o;
      const host = byGuid.get(o.wall_guid);
      return { ...o, start: onAxis(host, o.s_start), end: onAxis(host, o.s_end), storey_id: host?.storey_id ?? o.storey_id ?? null };
    });
  }
  // Zones (S6): their own ops, and a deleted room leaves every zone.
  const zones =
    model.zones && ops.some((op) => op.op.startsWith('zone.') || op.op.startsWith('space.')) ? applyZoneOps(model.zones, ops, spaces) : model.zones;
  // Stairs (S8): their own ops.
  const stairs = model.stairs && ops.some((op) => op.op.startsWith('stair.')) ? applyStairOps(model.stairs, ops) : model.stairs;
  return { ...model, walls, openings, spaces, storeys, slabs, zones, stairs };
}
